// ADO #572 — the dashboard's WhatsApp ask, modelled on CollegePrompt.
//
// Shown on every dashboard visit until the learner saves a number, never on the
// same visit as the college prompt (one ask per visit — DashboardPage decides).
// Leads with what they get, in plain words for a first-time programmer. The
// opt-in box starts UNTICKED: WhatsApp requires an explicit yes, and Save waits
// for a valid number and the tick. "Not now" skips; we ask again next visit.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MessageCircle, LifeBuoy, CalendarCheck, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { track } from '@/services/learnerEvents';
import { normaliseWhatsApp, saveWhatsApp } from '@/services/whatsappService';

export const BENEFITS = [
  // Not hints or "where you left off": the course already gives both, on the desktop it is used on (Ram,
  // 2026-10-15). WhatsApp adds what the site cannot: a person to ask, and a weekly look back.
  { icon: LifeBuoy, title: 'Help from a real person', body: 'Stuck even after the hints? Reply to us on WhatsApp and one of us will help you.' },
  { icon: CalendarCheck, title: 'Your progress each week', body: 'One short message with how far you have come.' },
];

// The country code field defaults to +91; the number field takes the rest. A
// learner who pastes a full +… number into the number field is taken at it.
export const combineNumber = (code, number) => {
  const n = String(number || '').trim();
  const c = String(code || '').trim();
  if (n.startsWith('+')) return n;
  if (!c || c === '+91') return n;
  return `${c.startsWith('+') ? c : `+${c}`}${n}`;
};

const WhatsAppPrompt = ({ onDone }) => {
  const [code, setCode] = useState('+91');
  const [number, setNumber] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Once per visit: the dashboard mounts this at most once per page load.
  useEffect(() => { track('whatsapp.prompt_shown', 'dashboard'); }, []);

  const e164 = normaliseWhatsApp(combineNumber(code, number));
  const canSave = !!e164 && agreed && !saving;

  const skip = () => {
    track('whatsapp.skipped', 'dashboard');
    onDone?.();
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      await saveWhatsApp(e164);
      track('whatsapp.saved', 'dashboard');
      toast.success("Thanks! We'll message you on WhatsApp about your course.");
      onDone?.({ saved: true });
    } catch (e) {
      setError(e.message || "Couldn't save that. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 p-6"
        role="dialog"
        aria-labelledby="whatsapp-prompt-title"
        data-testid="whatsapp-prompt"
      >
        <div className="flex items-start justify-between mb-1">
          <h2 id="whatsapp-prompt-title" className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-emerald-600" />
            Learn with help on WhatsApp
          </h2>
          <button
            onClick={skip}
            aria-label="Close"
            className="p-1.5 -mr-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Share your WhatsApp number and we'll help you along the way.
        </p>

        <ul className="space-y-3 mb-4">
          {BENEFITS.map(({ icon: Icon, title, body }) => (
            <li key={title} className="flex items-start gap-3">
              <span className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <Icon className="h-4 w-4" />
              </span>
              <div className="text-sm">
                <div className="font-medium text-slate-800">{title}</div>
                <div className="text-slate-500">{body}</div>
              </div>
            </li>
          ))}
        </ul>

        <label htmlFor="whatsapp-number" className="text-sm font-medium text-slate-700">
          Your WhatsApp number
        </label>
        <div className="flex gap-2 mt-1.5">
          <Input
            aria-label="Country code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="h-11 w-20 text-center"
            inputMode="tel"
          />
          <Input
            id="whatsapp-number"
            autoFocus
            value={number}
            onChange={(e) => { setNumber(e.target.value); setError(''); }}
            placeholder="98765 43210"
            className="h-11 flex-1"
            inputMode="tel"
            autoComplete="tel-national"
          />
        </div>
        {error ? <p className="text-xs text-rose-600 mt-1.5">{error}</p> : null}

        <label className="mt-4 flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-emerald-600"
          />
          <span>Yes, message me on WhatsApp about my course</span>
        </label>
        <p className="text-xs text-slate-400 mt-1.5 pl-[26px]">
          Only about your course, never ads. You can stop anytime from your profile.
        </p>

        <Button
          onClick={save}
          disabled={!canSave}
          className="mt-4 w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>

        <button
          onClick={skip}
          className="mt-3 w-full text-center text-xs text-slate-400 hover:text-slate-600"
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default WhatsAppPrompt;
